/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { sanitized } from 'volta/utils/text-utils';

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { TCollectionPropertyForUI } from 'volta/models/types/collection-view';

interface CollectionViewPropertiesSignature {
  Args: {
    properties: TCollectionPropertyForUI[];
    groupBy?: string[];
    onChange?: (props: TCollectionPropertyForUI[]) => void;
  };
  Element: HTMLElement;
}

export default class CollectionViewPropertiesComponent extends Component<CollectionViewPropertiesSignature> {
  @tracked search?: string;

  get displayedProperties() {
    const toCheck = new Set(this.args.groupBy ?? []);
    return this.args.properties.filter((p) => !p.isStatic && !toCheck.has(p.property));
  }

  get filteredProperties() {
    const { search } = this;

    if (!search || search === '') {
      return this.displayedProperties;
    }
    const _search = sanitized(search);
    return this.displayedProperties.filter((p) => p.title && sanitized(p.title).includes(_search));
  }

  checkPropertyEnabled = (
    el?: Element,
    _target?: Element,
    _source?: Element,
    sibling?: Element
  ) => {
    return (
      !el?.querySelector('.collection-view-property[disabled]') &&
      !sibling?.querySelector('.collection-view-property[disabled]')
    );
  };

  toggleProperty = (item: TCollectionPropertyForUI) => {
    const newProps = this.args.properties.reduce<TCollectionPropertyForUI[]>((acc, prop) => {
      const isDisabled = item.property === prop.property ? !prop.disabled : prop.disabled;
      if (!isDisabled) {
        acc.push(prop);
      }
      return acc;
    }, []);

    this.args.onChange?.(newProps);
  };

  toggleIsFixed = (item: TCollectionPropertyForUI) => {
    const newProps = this.args.properties.reduce<TCollectionPropertyForUI[]>((acc, prop) => {
      if (!prop.disabled) {
        const isFixed =
          item.property === prop.property ? (prop.isFixed ? undefined : 'left') : prop.isFixed;
        acc.push({
          ...prop,
          isFixed
        });
      }
      return acc;
    }, []);

    this.args.onChange?.(
      newProps.sort((a, b) => {
        return Number(!!b.isFixed) - Number(!!a.isFixed);
      })
    );
  };

  reorderProperties = ([properties]: [TCollectionPropertyForUI[]]) => {
    const newProps = properties.reduce<TCollectionPropertyForUI[]>((acc, prop) => {
      if (!prop.disabled) {
        acc.push(prop);
      }
      return acc;
    }, []);

    const reorderedSet = new Set(newProps.map((p) => p.property));
    const propsWithoutReordered = this.args.properties.filter(
      (p) => !reorderedSet.has(p.property) && !p.disabled
    );

    this.args.onChange?.(newProps.concat(propsWithoutReordered));
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Properties': typeof CollectionViewPropertiesComponent;
    'collection-view/properties': typeof CollectionViewPropertiesComponent;
  }
}
